import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';
import type { MerchantOnboardingField, SetMerchantOnboardingFieldsPayload } from '../types';

/**
 * osprey-api `/merchant-onboarding/fields` (PE-2181 FE, backend PE-2180).
 *
 * TCS-staff only: the controller carries `CollectiveOnlyGuard` + `PermissionGuard` with
 * MERCHANT_ONBOARDING.READ at class level and MANAGE on the write. watchman already seeds
 * MERCHANT_ONBOARDING:[READ, MANAGE] for COLLECTIVE_SUPERADMIN/ADMIN, so no seed follow-up.
 *
 * Not merchant- or tenant-scoped: these are GLOBAL platform rows, and `/merchant-onboarding`
 * is deliberately absent from ApiFetch's MERCHANT_SCOPED_PREFIXES/TENANT_SCOPED_PREFIXES —
 * appending a stray `merchantId`/`tenantId` is the exact 422 break from 2026-09-02.
 *
 * ⚠️ There is no reset-to-default route. watchman exposes only GET + PUT (upsert) on
 * `/merchant-onboarding-fields`; defaults live server-side in
 * DEFAULT_ONBOARDING_FIELD_REQUIREMENTS and a row, once written, is never deleted. The
 * `isDefault` badge therefore flips to "Admin override" permanently on first save. A
 * `DELETE /merchant-onboarding/fields/:fieldKey` is filed as a backend ask; until it lands
 * the page deliberately offers no reset rather than faking one with a PUT.
 */
export const merchantOnboardingFieldsApi = {
  /** Every field key with its resolved requirement and whether that is the platform default. */
  list: async (): Promise<MerchantOnboardingField[]> => {
    const response = await ApiFetch.get<Enveloped<MerchantOnboardingField[]>>(
      '/merchant-onboarding/fields',
    );
    return unwrap(response);
  },

  /**
   * ⭐ A REAL PUT. `ApiFetch.put` issues PATCH — `legacy_put` is the one that issues PUT,
   * and osprey-api declares this route as `@Put('fields')`.
   *
   * Upserts only the listed keys; omitted keys keep their existing state. A 400 comes back
   * when an always-required field is sent as OPTIONAL — the UI disables that option, so a
   * 400 here means the server list and ALWAYS_REQUIRED_FIELD_KEYS have drifted apart.
   */
  setMany: async (
    payload: SetMerchantOnboardingFieldsPayload,
  ): Promise<MerchantOnboardingField[]> => {
    const response = await ApiFetch.legacy_put<Enveloped<MerchantOnboardingField[]>>(
      '/merchant-onboarding/fields',
      payload,
    );
    return unwrap(response);
  },
};
